import React, { useEffect, useMemo, useState } from "react";
import {
  COUNTED_SHIFT_TYPES,
  toIsoDate,
  weekStartOvernightRemainder,
} from "./chkHours";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const HOUR_HEIGHT = 28;
const DAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const LANE_MIN_WIDTH = 56;
const DAY_MIN_WIDTH = 80;
const HOURS_COL_WIDTH = 64;
const EMPTY_STATION = "Без станции";

const STATION_COLORS = [
  "#5b7c99",
  "#6f8f73",
  "#8a6d5b",
  "#6d688f",
  "#8a6170",
  "#5c8588",
];

function formatShortDate(dateStr) {
  if (!dateStr) return "";
  const [, m, d] = dateStr.split("-");
  return `${d}.${m}`;
}

function normalizeEnd(start, end) {
  const s = Number(start);
  const e = Number(end);
  if (Number.isNaN(s) || Number.isNaN(e)) return null;
  if (e === 0) return 24;
  return e;
}

function formatShiftTime(start, end) {
  const endLabel = Number(end) === 0 ? 24 : end;
  return `${start}–${endLabel}`;
}

function splitShiftSegments(shift, weekDates) {
  const start = Number(shift.start);
  const endRaw = normalizeEnd(shift.start, shift.end);
  if (endRaw === null || Number.isNaN(start)) return [];

  const dayIndex = weekDates.findIndex(
    (day) => toIsoDate(day) === toIsoDate(shift.date),
  );
  if (dayIndex < 0) {
    const leftover = weekStartOvernightRemainder(shift, weekDates);
    if (!leftover) return [];
    const label = `${shift.user_surname ?? ""} ${shift.user_name ?? ""}`.trim();
    return [
      {
        key: `${shift.id}-week-start`,
        shiftId: shift.id,
        employeeId: shift.employee,
        dayIndex: leftover.dayIndex,
        startHour: leftover.startHour,
        endHour: leftover.endHour,
        label,
        station: shift.station_name || "",
        timeLabel: formatShiftTime(start, shift.end),
        barTime: null,
        part: "overnight-end",
        shift,
      },
    ];
  }

  const label = `${shift.user_surname ?? ""} ${shift.user_name ?? ""}`.trim();
  const station = shift.station_name || "";
  const employeeId = shift.employee;
  const timeLabel = formatShiftTime(start, shift.end);

  // Обычная смена в пределах дня
  if (endRaw > start) {
    return [
      {
        key: `${shift.id}-day`,
        shiftId: shift.id,
        employeeId,
        dayIndex,
        startHour: start,
        endHour: endRaw,
        label,
        station,
        timeLabel,
        barTime: null,
        part: "day",
        shift,
      },
    ];
  }

  // Ночная: визуально start → 24 сегодня и 0 → end завтра,
  // но на вечернем куске пишем реальное окончание (22–6, не 22–24).
  const segments = [
    {
      key: `${shift.id}-night-a`,
      shiftId: shift.id,
      employeeId,
      dayIndex,
      startHour: start,
      endHour: 24,
      label,
      station,
      timeLabel,
      barTime: timeLabel,
      part: "overnight-start",
      shift,
    },
  ];

  if (endRaw > 0 && dayIndex < weekDates.length - 1) {
    segments.push({
      key: `${shift.id}-night-b`,
      shiftId: shift.id,
      employeeId,
      dayIndex: dayIndex + 1,
      startHour: 0,
      endHour: endRaw,
      label,
      station,
      timeLabel,
      barTime: null,
      part: "overnight-end",
      shift,
    });
  }

  return segments;
}

function getShiftStationKey(shift, templateById) {
  const template = templateById.get(Number(shift.template_shift_id));
  return (
    template?.group_station_name ||
    shift.station_name ||
    template?.station_name ||
    EMPTY_STATION
  );
}

/**
 * Assign lanes within each day so overlapping employee bars don't stack on top of each other.
 */
function assignLanes(segments) {
  const byDay = {};
  segments.forEach((seg) => {
    if (!byDay[seg.dayIndex]) byDay[seg.dayIndex] = [];
    byDay[seg.dayIndex].push(seg);
  });

  const laneCountByDay = {};
  const withLanes = [];

  Object.keys(byDay).forEach((dayKey) => {
    const dayIndex = Number(dayKey);
    const daySegs = byDay[dayIndex].sort((a, b) => {
      if (a.startHour !== b.startHour) return a.startHour - b.startHour;
      return a.endHour - b.endHour;
    });

    const laneEnds = [];
    daySegs.forEach((seg) => {
      let lane = laneEnds.findIndex((end) => end <= seg.startHour);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(seg.endHour);
      } else {
        laneEnds[lane] = seg.endHour;
      }
      withLanes.push({ ...seg, lane });
    });
    laneCountByDay[dayIndex] = Math.max(laneEnds.length, 1);
  });

  return { segments: withLanes, laneCountByDay };
}

const ScheduleChkEmployeeTimeline = ({ schedule, weekDates = [] }) => {
  const shifts = useMemo(
    () =>
      (schedule?.shifts || []).filter((shift) =>
        COUNTED_SHIFT_TYPES.has(Number(shift.type_shift_id)),
      ),
    [schedule?.shifts],
  );
  const templates = schedule?.ts || [];

  const templateById = useMemo(() => {
    const map = new Map();
    templates.forEach((ts) => {
      if (ts?.id != null) map.set(Number(ts.id), ts);
    });
    return map;
  }, [templates]);

  const employees = useMemo(() => {
    const map = new Map();
    shifts.forEach((shift) => {
      if (!shift?.employee) return;
      if (!map.has(shift.employee)) {
        map.set(shift.employee, {
          id: shift.employee,
          name: `${shift.user_surname ?? ""} ${shift.user_name ?? ""}`.trim() ||
            `ID ${shift.employee}`,
          post: shift.user_post_name || "",
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "ru"),
    );
  }, [shifts]);

  const stations = useMemo(() => {
    const names = new Set();
    shifts.forEach((shift) => {
      names.add(getShiftStationKey(shift, templateById));
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, "ru"));
  }, [shifts, templateById]);

  const [visibleIds, setVisibleIds] = useState({});
  const [visibleStations, setVisibleStations] = useState({});
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    setVisibleIds((prev) => {
      const next = {};
      employees.forEach((emp) => {
        next[emp.id] = prev[emp.id] ?? true;
      });
      return next;
    });
  }, [employees]);

  useEffect(() => {
    setVisibleStations((prev) => {
      const next = {};
      stations.forEach((name) => {
        next[name] = prev[name] ?? true;
      });
      return next;
    });
  }, [stations]);

  const colorByStation = useMemo(() => {
    const map = {};
    stations.forEach((name, index) => {
      map[name] = STATION_COLORS[index % STATION_COLORS.length];
    });
    return map;
  }, [stations]);

  const { segments, laneCountByDay } = useMemo(() => {
    const all = [];
    shifts.forEach((shift) => {
      if (!visibleIds[shift.employee]) return;
      const stationKey = getShiftStationKey(shift, templateById);
      if (!visibleStations[stationKey]) return;
      all.push(
        ...splitShiftSegments(shift, weekDates).map((seg) => ({
          ...seg,
          stationKey,
        })),
      );
    });
    return assignLanes(all);
  }, [shifts, weekDates, visibleIds, visibleStations, templateById]);

  const segmentsByDay = useMemo(() => {
    const map = {};
    weekDates.forEach((_, i) => {
      map[i] = [];
    });
    segments.forEach((seg) => {
      if (!map[seg.dayIndex]) map[seg.dayIndex] = [];
      map[seg.dayIndex].push(seg);
    });
    return map;
  }, [segments, weekDates]);

  const toggleEmployee = (id) => {
    setVisibleIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const setAllVisible = (value) => {
    const next = {};
    employees.forEach((emp) => {
      next[emp.id] = value;
    });
    setVisibleIds(next);
  };

  const toggleStation = (name) => {
    setVisibleStations((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const setAllStationsVisible = (value) => {
    const next = {};
    stations.forEach((name) => {
      next[name] = value;
    });
    setVisibleStations(next);
  };

  const dayMinWidths = weekDates.map(
    (_, index) =>
      Math.max(DAY_MIN_WIDTH, (laneCountByDay[index] || 1) * LANE_MIN_WIDTH),
  );
  const daysMinWidth = dayMinWidths.reduce((sum, width) => sum + width, 0);
  const dayColumns = dayMinWidths
    .map((width) => `minmax(${width}px, 1fr)`)
    .join(" ");

  if (!weekDates.length) {
    return null;
  }

  if (!employees.length) {
    return (
      <div className="schedule-chk-empty">
        Нет смен сотрудников за выбранную неделю
      </div>
    );
  }

  const gridHeight = HOURS.length * HOUR_HEIGHT;

  return (
    <div className="chk-emp-timeline">
      <div className="chk-emp-timeline-header">
        <div>
          <h4 className="chk-emp-timeline-title">График по сотрудникам</h4>
          <p className="chk-emp-timeline-hint">
            По вертикали — часы, по горизонтали — дни недели. Отфильтруйте станции, если полосок слишком много.
          </p>
        </div>
      </div>

      <div className="chk-emp-timeline-filters">
        <div className="chk-emp-filter-row">
          <span className="chk-emp-filter-label">Сотрудники</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setAllVisible(true)}
          >
            Все
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setAllVisible(false)}
          >
            Скрыть
          </button>
          {employees.map((emp) => {
            const active = visibleIds[emp.id];
            return (
              <label
                key={emp.id}
                className={`chk-emp-filter ${active ? "is-active" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={Boolean(active)}
                  onChange={() => toggleEmployee(emp.id)}
                />
                <span className="chk-emp-filter-name">{emp.name}</span>
              </label>
            );
          })}
        </div>
        {stations.length > 0 && (
          <div className="chk-emp-filter-row">
            <span className="chk-emp-filter-label">Станции</span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setAllStationsVisible(true)}
            >
              Все
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setAllStationsVisible(false)}
            >
              Скрыть
            </button>
            {stations.map((name) => {
              const active = visibleStations[name];
              return (
                <label
                  key={name}
                  className={`chk-emp-filter ${active ? "is-active" : ""}`}
                  style={{ "--emp-color": colorByStation[name] }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(active)}
                    onChange={() => toggleStation(name)}
                  />
                  <span className="chk-emp-filter-dot" />
                  <span className="chk-emp-filter-name">{name}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="chk-emp-timeline-scroll">
        <div
          className="chk-emp-timeline-board"
          style={{
            "--day-columns": dayColumns,
            "--days-min-width": `${daysMinWidth}px`,
            "--board-min-width": `${HOURS_COL_WIDTH + daysMinWidth}px`,
          }}
        >
          <div className="chk-emp-hours-col">
            <div className="chk-emp-corner">Час</div>
            <div className="chk-emp-hours" style={{ height: gridHeight }}>
              {HOURS.map((hour) => (
                <div
                  key={hour}
                  className="chk-emp-hour-label"
                  style={{ height: HOUR_HEIGHT }}
                >
                  {String(hour).padStart(2, "0")}:00
                </div>
              ))}
            </div>
          </div>

          <div className="chk-emp-days">
            <div className="chk-emp-days-head">
              {weekDates.map((date, index) => (
                <div key={date || index} className="chk-emp-day-head">
                  <span className="chk-emp-day-name">{DAY_LABELS[index]}</span>
                  <span className="chk-emp-day-date">{formatShortDate(date)}</span>
                </div>
              ))}
            </div>

            <div className="chk-emp-days-body" style={{ height: gridHeight }}>
              {weekDates.map((date, dayIndex) => {
                const laneCount = laneCountByDay[dayIndex] || 1;
                const daySegs = segmentsByDay[dayIndex] || [];
                return (
                  <div key={date || dayIndex} className="chk-emp-day-col">
                    <div className="chk-emp-day-grid">
                      {HOURS.map((hour) => (
                        <div
                          key={hour}
                          className="chk-emp-hour-line"
                          style={{ height: HOUR_HEIGHT }}
                        />
                      ))}
                    </div>
                    <div className="chk-emp-day-bars">
                      {daySegs.map((seg) => {
                        const top = seg.startHour * HOUR_HEIGHT;
                        const height = Math.max(
                          (seg.endHour - seg.startHour) * HOUR_HEIGHT - 2,
                          HOUR_HEIGHT - 2,
                        );
                        const widthPct = 100 / laneCount;
                        const leftPct = seg.lane * widthPct;
                        const color = colorByStation[seg.stationKey];
                        const isHovered =
                          hovered &&
                          (hovered.key === seg.key ||
                            hovered.shiftId === seg.shiftId);
                        const employee = employees.find(
                          (emp) => emp.id === seg.employeeId,
                        );

                        return (
                          <div
                            key={seg.key}
                            className={`chk-emp-bar ${
                              isHovered ? "is-hovered" : ""
                            } ${
                              seg.part === "overnight-start"
                                ? "is-overnight-start"
                                : ""
                            } ${
                              seg.part === "overnight-end"
                                ? "is-overnight-end"
                                : ""
                            }`}
                            style={{
                              top,
                              height,
                              left: `calc(${leftPct}% + 2px)`,
                              width: `calc(${widthPct}% - 4px)`,
                              background: color,
                            }}
                            onMouseEnter={(event) =>
                              setHovered({
                                key: seg.key,
                                shiftId: seg.shiftId,
                                x: event.clientX,
                                y: event.clientY,
                                name: seg.label,
                                post: employee?.post || "",
                                time: seg.timeLabel,
                                station: seg.station,
                              })
                            }
                            onMouseMove={(event) =>
                              setHovered((prev) =>
                                prev
                                  ? { ...prev, x: event.clientX, y: event.clientY }
                                  : prev,
                              )
                            }
                            onMouseLeave={() => setHovered(null)}
                          >
                            {seg.barTime ? (
                              <span className="chk-emp-bar-time">
                                {seg.barTime}
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {hovered && (
        <div
          className="chk-emp-tooltip"
          style={{
            left: Math.min(hovered.x + 12, window.innerWidth - 280),
            top: Math.min(hovered.y + 12, window.innerHeight - 96),
          }}
        >
          <strong>{hovered.name}</strong>
          {hovered.post ? <span>{hovered.post}</span> : null}
          <span>
            {hovered.time}
            {hovered.station ? ` · ${hovered.station}` : ""}
          </span>
        </div>
      )}
    </div>
  );
};

export default ScheduleChkEmployeeTimeline;
